import React, { useEffect, useState } from 'react';
import type { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/store';
import { getSessionId } from '../../store/sessionSlice';
import {
  getAccountData,
  fetchAccountList,
  getAccountListLoading,
} from '../../store/accountListSlice';
import { getStaticFeatures } from '../../store/featureSlice';
import {
  filterAccountsByAllowedFeatures,
  getAccountsSorted,
  filterAccountsWithNoFeatures,
} from '../../utils/helpers';
import { capturePageLoadAnalytics } from '../../utils/analytics/analyticsDispatchers';
import { MY_CARDS_EVENT } from '../../utils/analytics/analyticsEvents';
import WelcomeMessage from '../welcomeMessage/WelcomeMessage';
import MyCards from './myCards/MyCards';
import AccountAccordion from './AccountAccordion';
import SkeletonLoader from '../loader/SkeletonLoader';
import './accountList.css';

const AccountList: FunctionComponent = () => {
  const accounts: any = useSelector(getAccountData);
  const isAccountLoading = useSelector(getAccountListLoading);
  const features = useSelector(getStaticFeatures);
  const isSessionCreated = useSelector(getSessionId);

  const [matchedAccounts, setMatchedAccounts]: any = useState([]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const navigateNoAccountsFound = () => navigate('/account-not-found');

  useEffect(() => {
    if (isSessionCreated) {
      dispatch(fetchAccountList());
    }
  }, [isSessionCreated]);

  useEffect(() => {
    isSessionCreated && capturePageLoadAnalytics(MY_CARDS_EVENT);
  }, [isSessionCreated]);

  useEffect(() => {
    /**
     * ACCOUNTS IN API RESPONSE MODIFIED IN BELOW STAGES:
     * Stage-1: Account feature properties, and control center features compare and
     * overwrite in account list as a output updateAndFilteredAccounts
     * accounts => Fetched accounts from API endpoint
     * features => Fetched Features from control center
     * */
    if (accounts.length > 0 && features.length > 0) {
      const updateAndFilteredAccounts: any = filterAccountsByAllowedFeatures(
        accounts,
        features,
      );
      const accountsWithFeatures = filterAccountsWithNoFeatures(
        updateAndFilteredAccounts,
      );

      accountsWithFeatures.length === 0 && navigateNoAccountsFound();
      /**
       * Stage-2: Account sorted with getAccountSorted, as lead account are first in the list,
       * and rest accounts are in alphabetical order
       */
      const finalAccountList = getAccountsSorted(accountsWithFeatures);

      /**
       * when no lead account present then finalAccountList will be set to empty
       * as no other accounts will be displayed for the user
       * and user will go to account not found page.
       */
      finalAccountList.length === 0 && navigateNoAccountsFound();
      // setup the final sorted/filtered account list in local state
      setMatchedAccounts(finalAccountList);
    }
  }, [accounts, features]);

  return (
    <>
      {isAccountLoading ? (
        <SkeletonLoader />
      ) : (
        matchedAccounts.length > 0 && (
          <>
            <div className="page-title-wrapper">
              <>
                <WelcomeMessage
                  firstName={matchedAccounts[0]['cipher.firstName']}
                />
                <MyCards />
              </>
            </div>
            <div
              className="accountlist-section"
              data-testid="accountlist-section"
            >
              <AccountAccordion accounts={matchedAccounts} />
            </div>
          </>
        )
      )}
    </>
  );
};

export default AccountList;
